import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useEstimateState } from "../EstimateContext";
import {
  getAllSections,
  calculateConstructionDesign,
  updateContextWithCalculationResults,
} from "../../../../../api/projectEstimatesApi";
import "./ContractSectionsTab.css";

const ContractSectionsTab = forwardRef(
  ({ projectId, isSaving = false, isEditable = true }, ref) => {
    const { estimateData, setEstimateData, sectionDetails } =
      useEstimateState();

    // Состояние для разделов на договоре
    const [contractSections, setContractSections] = useState([]);
    const [editingSections, setEditingSections] = useState({});
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [sectionsList, setSectionsList] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);
    const [loadingSections, setLoadingSections] = useState(false);
    const [sectionSearch, setSectionSearch] = useState("");
    // Флаг для отслеживания необходимости калькуляции
    const shouldCalculateRef = useRef(false);
    const isInitialLoadRef = useRef(true);
    // Сохраняем предыдущее значение contract_sections для сравнения
    const prevContractSectionsRef = useRef(null);

    // Функция для нормализации структуры секции (поддержка старого и нового формата)
    const normalizeSection = (section) => {
      // Если уже есть поле section, возвращаем как есть
      if (section.section) {
        return section;
      }
      // Если есть прямые поля id и name, преобразуем в новый формат
      if (section.id && section.name) {
        return {
          section: {
            id: section.id,
            name: section.name,
            ...(section.description != null && {
              description: section.description,
            }),
          },
          amount: section.amount || 0,
        };
      }
      // Если есть section_id, создаем объект section
      if (section.section_id) {
        return {
          section: {
            id: section.section_id,
            name: section.name || "",
            ...(section.description != null && {
              description: section.description,
            }),
          },
          amount: section.amount || 0,
        };
      }
      // Возвращаем как есть, если структура неизвестна
      return section;
    };

    // Загружаем данные разделов на договоре из estimateData
    useEffect(() => {
      if (estimateData) {
        // Поддержка новой структуры: contract_sections.sections или старого массива
        let sectionsArray = [];
        if (estimateData.contract_sections) {
          if (
            estimateData.contract_sections.sections &&
            Array.isArray(estimateData.contract_sections.sections)
          ) {
            // Новая структура: объект с полем sections
            sectionsArray = estimateData.contract_sections.sections;
          } else if (Array.isArray(estimateData.contract_sections)) {
            // Старая структура: массив
            sectionsArray = estimateData.contract_sections;
          }
        }

        const sections = sectionsArray.map(normalizeSection);
        setContractSections(sections);
        // После первой загрузки помечаем, что начальная загрузка завершена
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      }
    }, [estimateData]);

    // Выполняем калькуляцию при изменении contract_sections в estimateData
    useEffect(() => {
      // Пропускаем первую загрузку
      if (isInitialLoadRef.current) {
        if (estimateData?.contract_sections) {
          // Поддержка новой структуры: contract_sections.sections или старого массива
          let sectionsArray = [];
          if (
            estimateData.contract_sections.sections &&
            Array.isArray(estimateData.contract_sections.sections)
          ) {
            sectionsArray = estimateData.contract_sections.sections;
          } else if (Array.isArray(estimateData.contract_sections)) {
            sectionsArray = estimateData.contract_sections;
          }
          prevContractSectionsRef.current = JSON.stringify(sectionsArray);
        }
        return;
      }

      // Проверяем, изменились ли contract_sections
      // Поддержка новой структуры: contract_sections.sections или старого массива
      let currentSectionsArray = [];
      if (estimateData?.contract_sections) {
        if (
          estimateData.contract_sections.sections &&
          Array.isArray(estimateData.contract_sections.sections)
        ) {
          currentSectionsArray = estimateData.contract_sections.sections;
        } else if (Array.isArray(estimateData.contract_sections)) {
          currentSectionsArray = estimateData.contract_sections;
        }
      }

      const currentContractSections = JSON.stringify(currentSectionsArray);
      const prevContractSections = prevContractSectionsRef.current;

      // Если contract_sections не изменились или калькуляция не нужна, выходим
      if (
        currentContractSections === prevContractSections ||
        !shouldCalculateRef.current
      ) {
        return;
      }

      // Обновляем предыдущее значение
      prevContractSectionsRef.current = currentContractSections;

      // Сбрасываем флаг перед вызовом калькуляции
      shouldCalculateRef.current = false;

      // Выполняем калькуляцию напрямую с обновленными данными
      if (projectId && estimateData && sectionDetails) {
        const performCalculation = async () => {
          try {
            // Вызываем калькуляцию с текущими данными из контекста
            const calculationResult = await calculateConstructionDesign(
              projectId,
              estimateData,
              sectionDetails,
            );

            // Обновляем контекст с результатами калькуляции
            const updatedContext = updateContextWithCalculationResults(
              { estimate: estimateData, sections: sectionDetails },
              calculationResult,
            );

            // Обновляем данные в контексте
            setEstimateData(updatedContext.estimate);
          } catch (error) {
            console.error("Ошибка при выполнении калькуляции:", error);
          }
        };

        performCalculation();
      }
    }, [estimateData, projectId, sectionDetails, setEstimateData]);

    // Функция для вычисления итоговой суммы
    const calculateTotal = () => {
      return contractSections.reduce((sum, section) => {
        return sum + (parseFloat(section.amount) || 0);
      }, 0);
    };

    // Вспомогательная функция для получения ID секции
    const getSectionId = (section) => {
      return section.section?.id || section.id;
    };

    // Вспомогательная функция для получения имени секции
    const getSectionName = (section) => {
      return section.section?.name || section.name;
    };

    // Вспомогательная функция для получения описания секции
    const getSectionDescription = (section) => {
      return section.section?.description ?? section.description ?? "";
    };

    // Функция для начала редактирования суммы раздела
    const startEditAmount = (sectionId) => {
      if (!isEditable || isSaving) return;
      setEditingSections((prev) => ({
        ...prev,
        [sectionId]: true,
      }));
    };

    // Функция для сохранения суммы раздела
    const saveAmount = (sectionId, newAmount) => {
      const amount = parseFloat(newAmount) || 0;
      const updatedSections = contractSections.map((section) => {
        const currentId = getSectionId(section);
        return currentId === sectionId ? { ...section, amount } : section;
      });

      setContractSections(updatedSections);
      setEditingSections((prev) => {
        const newState = { ...prev };
        delete newState[sectionId];
        return newState;
      });

      // Обновляем данные в estimateData
      if (setEstimateData) {
        // Устанавливаем флаг для калькуляции перед обновлением данных
        shouldCalculateRef.current = true;
        setEstimateData((prev) => {
          if (!prev) return prev;
          // Сохраняем в новой структуре: объект с sections и total_amount
          const totalAmount = updatedSections.reduce((sum, section) => {
            return sum + (parseFloat(section.amount) || 0);
          }, 0);

          return {
            ...prev,
            contract_sections: {
              sections: updatedSections,
              total_amount: totalAmount,
            },
          };
        });
      }
    };

    // Функция для отмены редактирования
    const cancelEditAmount = (sectionId) => {
      setEditingSections((prev) => {
        const newState = { ...prev };
        delete newState[sectionId];
        return newState;
      });
    };

    // Функция для открытия модального окна добавления раздела
    const handleAddSection = async () => {
      if (!isEditable || isSaving) return;
      setShowAddSectionModal(true);
      setSelectedSections([]);
      setSectionSearch("");
      setLoadingSections(true);
      try {
        const data = await getAllSections();
        setSectionsList(data || []);
      } catch (e) {
        setSectionsList([]);
      } finally {
        setLoadingSections(false);
      }
    };

    // Функция для закрытия модального окна
    const handleCancelAddSection = () => {
      setShowAddSectionModal(false);
      setSelectedSections([]);
      setSectionSearch("");
    };

    // Функция для подтверждения добавления раздела
    const handleConfirmAddSection = () => {
      if (selectedSections.length === 0) return;

      const toAdd = sectionsList.filter((s) => selectedSections.includes(s.id));
      if (toAdd.length === 0) return;

      const newSections = toAdd.map((s) => ({
        section: {
          id: s.id,
          name: s.name,
          ...(s.description != null && { description: s.description }),
        },
        amount: 0,
      }));

      const updatedSections = [...contractSections, ...newSections];
      setContractSections(updatedSections);

      // Обновляем данные в estimateData
      if (setEstimateData) {
        shouldCalculateRef.current = true;
        setEstimateData((prev) => {
          if (!prev) return prev;
          const totalAmount = updatedSections.reduce((sum, section) => {
            return sum + (parseFloat(section.amount) || 0);
          }, 0);
          return {
            ...prev,
            contract_sections: {
              sections: updatedSections,
              total_amount: totalAmount,
            },
          };
        });
      }

      setShowAddSectionModal(false);
      setSelectedSections([]);
      setSectionSearch("");
    };

    // Функция для удаления раздела
    const handleDeleteSection = (sectionId) => {
      if (!isEditable || isSaving) return;
      if (!window.confirm("Вы уверены, что хотите удалить этот раздел?")) {
        return;
      }

      const updatedSections = contractSections.filter(
        (s) => getSectionId(s) !== sectionId,
      );
      setContractSections(updatedSections);

      // Обновляем данные в estimateData
      if (setEstimateData) {
        shouldCalculateRef.current = true;
        setEstimateData((prev) => {
          if (!prev) return prev;
          const totalAmount = updatedSections.reduce((sum, section) => {
            return sum + (parseFloat(section.amount) || 0);
          }, 0);
          return {
            ...prev,
            contract_sections: {
              sections: updatedSections,
              total_amount: totalAmount,
            },
          };
        });
      }
    };

    // Методы для ref
    useImperativeHandle(ref, () => ({
      getContractSections: () => contractSections,
      syncDataBeforeSave: () => {
        // Синхронизация данных перед сохранением
        if (setEstimateData) {
          setEstimateData((prev) => {
            if (!prev) return prev;
            // Сохраняем в новой структуре: объект с sections и total_amount
            const totalAmount = contractSections.reduce((sum, section) => {
              return sum + (parseFloat(section.amount) || 0);
            }, 0);

            return {
              ...prev,
              contract_sections: {
                sections: contractSections,
                total_amount: totalAmount,
              },
            };
          });
        }
      },
    }));

    const totalAmount = calculateTotal();

    return (
      <div className="contract-sections-tab">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <button
            onClick={handleAddSection}
            disabled={!isEditable || isSaving}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#fff",
              color: "#2A5E38",
              border: "none",
              borderRadius: "4px",
              cursor: !isEditable || isSaving ? "not-allowed" : "pointer",
              opacity: !isEditable || isSaving ? 0.6 : 1,
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
            Добавить раздел
          </button>
        </div>

        <div className="contract-sections-table">
          <table
            style={{
              width: "auto",
              borderCollapse: "collapse",
              minWidth: "600px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#333",
                    minWidth: "200px",
                  }}
                >
                  Раздел
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#333",
                    minWidth: "200px",
                  }}
                >
                  Прямые затраты
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#333",
                    width: "60px",
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {contractSections.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Нет разделов. Нажмите "Добавить раздел" для добавления.
                  </td>
                </tr>
              ) : (
                <>
                  {contractSections.map((section) => {
                    const sectionId = getSectionId(section);
                    const sectionName = getSectionName(section);
                    const sectionDescription = getSectionDescription(section);
                    return (
                      <tr
                        key={sectionId}
                        style={{
                          borderBottom: "1px solid #e9ecef",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#333",
                          }}
                        >
                          {sectionName}
                          {sectionDescription && (
                            <span
                              style={{
                                display: "block",
                                fontSize: "12px",
                                color: "#666",
                                marginTop: "2px",
                              }}
                            >
                              {sectionDescription}
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "right",
                          }}
                        >
                          {editingSections[sectionId] ? (
                            <input
                              type="number"
                              defaultValue={section.amount || 0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  saveAmount(sectionId, e.target.value);
                                } else if (e.key === "Escape") {
                                  cancelEditAmount(sectionId);
                                }
                              }}
                              autoFocus
                              style={{
                                width: "120px",
                                padding: "6px 10px",
                                border: "1.5px dashed #B8C2D1",
                                borderRadius: "6px",
                                background: "#FFFFFF",
                                fontSize: "14px",
                                textAlign: "right",
                                outline: "none",
                                transition: "border-color 0.2s",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = "#007bff";
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = "#B8C2D1";
                                saveAmount(sectionId, e.target.value);
                              }}
                              onClick={(e) => {
                                const input = e.target;
                                requestAnimationFrame(() => input.select());
                              }}
                              disabled={!isEditable || isSaving}
                            />
                          ) : (
                            <span
                              style={{
                                fontSize: "14px",
                                fontFamily: "Manrope, sans-serif",
                                display: "inline-block",
                                minWidth: "100px",
                                padding: "6px 10px",
                                border: "1.5px dashed #B8C2D1",
                                borderRadius: "6px",
                                background:
                                  !isEditable || isSaving
                                    ? "#F4F6FA"
                                    : "#F9FBFF",
                                textAlign: "right",
                                cursor:
                                  !isEditable || isSaving
                                    ? "default"
                                    : "pointer",
                              }}
                              onClick={() => startEditAmount(sectionId)}
                            >
                              ₸{" "}
                              {typeof section.amount === "number"
                                ? Number(
                                    section.amount.toFixed(2),
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })
                                : parseFloat(
                                    section.amount || 0,
                                  ).toLocaleString()}
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "center",
                          }}
                        >
                          {isEditable && (
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteSection(sectionId)}
                              title="Удалить раздел"
                              disabled={isSaving}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: isSaving ? "not-allowed" : "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                color: "#dc3545",
                                opacity: isSaving ? 0.6 : 1,
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSaving) {
                                  e.target.style.backgroundColor = "#f8d7da";
                                  e.target.style.color = "#721c24";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSaving) {
                                  e.target.style.backgroundColor = "transparent";
                                  e.target.style.color = "#dc3545";
                                }
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h10.667z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Итоговая строка */}
                  <tr
                    style={{
                      borderTop: "2px solid #e9ecef",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      ИТОГО
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#333",
                      }}
                    >
                      ₸ {totalAmount.toLocaleString()}
                    </td>
                    <td style={{ padding: "12px 16px" }}></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Модальное окно добавления раздела */}
        {showAddSectionModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={handleCancelAddSection}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Добавление раздела
              </h3>

              {loadingSections ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px",
                    color: "#666",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      width: "20px",
                      height: "20px",
                      border: "2px solid #f3f3f3",
                      borderTop: "2px solid #007bff",
                      borderRadius: "50%",
                      marginRight: "12px",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  Загрузка разделов...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      Выберите раздел из списка*
                    </label>
                    <input
                      type="text"
                      placeholder="Поиск"
                      value={sectionSearch}
                      onChange={(e) => setSectionSearch(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        marginBottom: "10px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                        boxSizing: "border-box",
                      }}
                    />
                    <div
                      style={{
                        maxHeight: "240px",
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {sectionsList.length === 0 ? (
                        <div
                          style={{
                            padding: "16px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          Нет доступных разделов.
                        </div>
                      ) : (
                        (() => {
                          const searchLower = (sectionSearch || "")
                            .toLowerCase()
                            .trim();
                          const filtered = searchLower
                            ? sectionsList.filter((s) =>
                                (s.name || "").toLowerCase().includes(searchLower),
                              )
                            : sectionsList;
                          const alreadyAdded = new Set(
                            contractSections.map((s) => getSectionId(s)),
                          );
                          const available = filtered.filter(
                            (s) => !alreadyAdded.has(s.id),
                          );
                          if (available.length === 0) {
                            return (
                              <div
                                style={{
                                  padding: "16px",
                                  color: "#666",
                                  fontSize: "14px",
                                }}
                              >
                                {filtered.length === 0
                                  ? "Ничего не найдено"
                                  : "Все разделы уже добавлены."}
                              </div>
                            );
                          }
                          return available.map((section) => {
                            const checked = selectedSections.includes(section.id);
                            return (
                              <label
                                key={section.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  padding: "8px 6px",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setSelectedSections((prev) =>
                                      e.target.checked
                                        ? [...prev, section.id]
                                        : prev.filter((x) => x !== section.id),
                                    );
                                  }}
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span className="section-label-text">
                                  <span className="section-label-name">{section.name}</span>
                                  {section.description && (
                                    <span className="section-label-description">
                                      {section.description}
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          });
                        })()
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                      marginTop: "24px",
                    }}
                  >
                    <button
                      onClick={handleCancelAddSection}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        color: "#666",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleConfirmAddSection}
                      disabled={selectedSections.length === 0}
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor:
                          selectedSections.length > 0 ? "#2A5E38" : "#ccc",
                        color: "white",
                        cursor:
                          selectedSections.length > 0 ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        opacity: selectedSections.length > 0 ? 1 : 0.6,
                      }}
                    >
                      Добавить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

ContractSectionsTab.displayName = "ContractSectionsTab";

export default ContractSectionsTab;
